import { Notification } from "@/utils/notificationUtils";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { EnumerationType } from "../../../modules/enumerations/enums";
import { lifeInsuranceTariffApi } from "../../../modules/lifeinsurancetariff/api";
import {
  CreateUpdateLifeInsuranceTariff,
  CreateUpdateLifeInsuranceTariffGroup,
  LifeInsuranceTariffFilterPageRequest,
} from "../../../modules/lifeinsurancetariff/types";
import { UUID } from "../../../typings/global";
import { EntityObject, SearchPageRequest } from "../../types";
import { invalidateEnumeration } from "./enumerations";

export const LIFE_INSURANCE_TARIFF_QUERY_KEYS = {
  filterLifeInsuranceTariffGroups: (payload?: SearchPageRequest) =>
    payload ? ["filterLifeInsuranceTariffGroups", payload] : ["filterLifeInsuranceTariffGroups"],
  createLifeInsuranceTariffGroup: () => ["createLifeInsuranceTariffGroup"],
  updateLifeInsuranceTariffGroup: () => ["updateLifeInsuranceTariffGroup"],
  deleteLifeInsuranceTariffGroup: () => ["deleteLifeInsuranceTariffGroup"],
  filterLifeInsuranceTariffs: (payload?: LifeInsuranceTariffFilterPageRequest) =>
    payload ? ["filterLifeInsuranceTariffs", payload] : ["filterLifeInsuranceTariffs"],
  createLifeInsuranceTariff: () => ["createLifeInsuranceTariff"],
  updateLifeInsuranceTariff: () => ["updateLifeInsuranceTariff"],
  deleteLifeInsuranceTariff: () => ["deleteLifeInsuranceTariff"],
};

export const useFilterLifeInsuranceTariffGroups = (payload: SearchPageRequest) => {
  return useQuery({
    queryKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.filterLifeInsuranceTariffGroups(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await lifeInsuranceTariffApi.filterLifeInsuranceTariffGroups(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateLifeInsuranceTariffGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.createLifeInsuranceTariffGroup(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateLifeInsuranceTariffGroup) => {
      const { data } = await lifeInsuranceTariffApi.createLifeInsuranceTariffGroup(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.TARIFF_GROUPS);
      Notification.itemCreatedNotification();
      queryClient.invalidateQueries({ queryKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.filterLifeInsuranceTariffGroups() });
    },
  });
};

export const useUpdateLifeInsuranceTariffGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.updateLifeInsuranceTariffGroup(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateLifeInsuranceTariffGroup>) => {
      const { data } = await lifeInsuranceTariffApi.updateLifeInsuranceTariffGroup(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.TARIFF_GROUPS);
      Notification.itemUpdatedNotification();
      queryClient.invalidateQueries({ queryKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.filterLifeInsuranceTariffGroups() });
    },
  });
};

export const useDeleteLifeInsuranceTariffGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.deleteLifeInsuranceTariffGroup(),
    mutationFn: (id: UUID) => {
      return lifeInsuranceTariffApi.deleteLifeInsuranceTariffGroup({ id });
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.TARIFF_GROUPS);
      Notification.itemDeletedNotification();
      queryClient.invalidateQueries({ queryKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.filterLifeInsuranceTariffGroups() });
    },
  });
};

export const useFilterLifeInsuranceTariffs = (payload: LifeInsuranceTariffFilterPageRequest) => {
  return useQuery({
    queryKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.filterLifeInsuranceTariffs(payload),
    meta: { globalSpinner: false },
    queryFn: async () => {
      const { data } = await lifeInsuranceTariffApi.filterLifeInsuranceTariffs(payload);

      return data;
    },
    placeholderData: keepPreviousData,
  });
};

export const useCreateLifeInsuranceTariff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.filterLifeInsuranceTariffs(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: CreateUpdateLifeInsuranceTariff) => {
      const { data } = await lifeInsuranceTariffApi.createLifeInsuranceTariff(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.TARIFF_GROUPS);
      Notification.itemCreatedNotification();
      queryClient.invalidateQueries({ queryKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.filterLifeInsuranceTariffs() });
    },
  });
};

export const useUpdateLifeInsuranceTariff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.updateLifeInsuranceTariff(),
    meta: { globalSpinner: false },
    mutationFn: async (payload: EntityObject<CreateUpdateLifeInsuranceTariff>) => {
      const { data } = await lifeInsuranceTariffApi.updateLifeInsuranceTariff(payload);

      return data;
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.TARIFF_GROUPS);
      Notification.itemUpdatedNotification();
      queryClient.invalidateQueries({ queryKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.filterLifeInsuranceTariffs() });
    },
  });
};

export const useDeleteLifeInsuranceTariff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.deleteLifeInsuranceTariff(),
    mutationFn: (id: UUID) => {
      return lifeInsuranceTariffApi.deleteLifeInsuranceTariff({ id });
    },
    onSuccess: () => {
      invalidateEnumeration(EnumerationType.TARIFF_GROUPS);
      Notification.itemDeletedNotification();
      queryClient.invalidateQueries({ queryKey: LIFE_INSURANCE_TARIFF_QUERY_KEYS.filterLifeInsuranceTariffs() });
    },
  });
};
